import { Component, ChangeDetectionStrategy, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserPlan } from '../../models/user.model';

@Component({
  selector: 'app-header',
  standalone: true,
  template: `
<header class="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/95 text-slate-100 backdrop-blur-xl transition-colors [background-image:radial-gradient(circle_at_12%_8%,rgba(37,99,235,.22),transparent_38%),radial-gradient(circle_at_88%_22%,rgba(6,182,212,.14),transparent_36%)]">
  <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent"></div>

  <div class="container mx-auto px-4">
    <div class="flex h-20 items-center justify-between gap-3">
      <button
        type="button"
        (click)="onLogoClick()"
        class="group flex items-center gap-3 rounded-xl px-2 py-1 transition-colors hover:bg-white/5">
        <span class="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-indigo-900/30">
          <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none">
            <path d="M16.5 7.5A6.5 6.5 0 1 0 16.5 16.5" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
            <path d="M10 14.5L14.5 10" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </span>

        <span class="leading-none">
          <span class="block text-xl font-black tracking-tight text-white">Correct<span class="text-blue-400">ify</span></span>
          <span class="hidden text-[10px] uppercase tracking-[0.2em] text-blue-300/70 sm:block">Performance em Redação</span>
        </span>
      </button>

      <nav class="hidden items-center gap-2 lg:flex">
        <button
          type="button"
          (click)="onLogoClick()"
          class="rounded-xl px-3 py-2 text-sm font-semibold transition"
          [ngClass]="(!isPlansActive() && !isProfileActive()) ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'">
          Plataforma
        </button>
        <button
          type="button"
          (click)="onViewPlansClick()"
          class="rounded-xl px-3 py-2 text-sm font-semibold transition"
          [ngClass]="isPlansActive() ? 'bg-blue-500/20 text-blue-200 ring-1 ring-blue-300/30' : 'text-slate-300 hover:bg-white/5 hover:text-white'">
          Planos
        </button>
        @if (isAuthenticated()) {
          <button
            type="button"
            (click)="onViewProfileClick()"
            class="rounded-xl px-3 py-2 text-sm font-semibold transition"
            [ngClass]="isProfileActive() ? 'bg-blue-500/20 text-blue-200 ring-1 ring-blue-300/30' : 'text-slate-300 hover:bg-white/5 hover:text-white'">
            Perfil
          </button>
        }
      </nav>

      <div class="hidden items-center gap-2 sm:flex">
        @if (isAuthenticated()) {
          @if (isAdmin()) {
            <span class="rounded-full border border-rose-400/30 bg-rose-500/15 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-rose-200">
              Admin
            </span>
          } @else {
            @if (plan()) {
              <span [class]="getPlanClasses(plan())" class="rounded-full px-2.5 py-1 text-xs font-black uppercase tracking-wide">
                {{ getPlanLabel(plan()) }}
              </span>
            }
            <span class="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
              <svg class="h-4 w-4 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.86-9.81a.75.75 0 0 0-1.21-.88l-3.49 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.06 0l4-5.5Z" clip-rule="evenodd" />
              </svg>
              <span class="font-extrabold text-white">@if (credits() > 900) { &infin; } @else { {{ credits() }} }</span>
              <span class="text-slate-400">créditos</span>
            </span>
          }

          <button
            type="button"
            (click)="onLogoutClick()"
            class="rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white">
            Sair
          </button>
        } @else {
          <button
            type="button"
            (click)="onRegisterClick()"
            class="rounded-xl px-4 py-2 text-sm font-semibold text-slate-300 transition hover:bg-white/5 hover:text-white">
            Criar conta
          </button>
          <button
            type="button"
            (click)="onLoginClick()"
            class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-md shadow-blue-900/40 transition hover:brightness-110">
            Login
            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.64l-4.16-3.96a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.16-3.96H3.75A.75.75 0 0 1 3 10Z" clip-rule="evenodd" />
            </svg>
          </button>
        }
      </div>

      <button
        type="button"
        class="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700 bg-slate-900 text-slate-200 transition hover:border-slate-500 hover:text-white sm:hidden"
        [attr.aria-expanded]="isMobileMenuOpen()"
        [attr.aria-label]="isMobileMenuOpen() ? 'Fechar menu' : 'Abrir menu'"
        (click)="toggleMobileMenu()">
        @if (!isMobileMenuOpen()) {
          <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M2.5 5.75A.75.75 0 0 1 3.25 5h13.5a.75.75 0 0 1 0 1.5H3.25a.75.75 0 0 1-.75-.75Zm0 4.25a.75.75 0 0 1 .75-.75h13.5a.75.75 0 0 1 0 1.5H3.25a.75.75 0 0 1-.75-.75Zm.75 3.5a.75.75 0 0 0 0 1.5h13.5a.75.75 0 0 0 0-1.5H3.25Z" clip-rule="evenodd"/>
          </svg>
        } @else {
          <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.47 4.47a.75.75 0 0 1 1.06 0L10 8.94l4.47-4.47a.75.75 0 1 1 1.06 1.06L11.06 10l4.47 4.47a.75.75 0 0 1-1.06 1.06L10 11.06l-4.47 4.47a.75.75 0 1 1-1.06-1.06L8.94 10 4.47 5.53a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/>
          </svg>
        }
      </button>
    </div>
  </div>

  @if (isMobileMenuOpen()) {
    <div class="border-t border-slate-800/80 bg-slate-950/95 px-4 pb-4 pt-3 sm:hidden [background-image:radial-gradient(circle_at_12%_8%,rgba(37,99,235,.20),transparent_40%),radial-gradient(circle_at_88%_22%,rgba(6,182,212,.12),transparent_38%)]">
      <div class="space-y-2">
        <button
          type="button"
          (click)="onMobileLandingClick()"
          class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold transition"
          [ngClass]="(!isPlansActive() && !isProfileActive()) ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'">
          Plataforma
        </button>
        <button
          type="button"
          (click)="onMobilePlansClick()"
          class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold transition"
          [ngClass]="isPlansActive() ? 'bg-blue-500/20 text-blue-200 ring-1 ring-blue-300/30' : 'text-slate-300 hover:bg-white/5 hover:text-white'">
          Planos
        </button>
        @if (isAuthenticated()) {
          <button
            type="button"
            (click)="onMobileProfileClick()"
            class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold transition"
            [ngClass]="isProfileActive() ? 'bg-blue-500/20 text-blue-200 ring-1 ring-blue-300/30' : 'text-slate-300 hover:bg-white/5 hover:text-white'">
            Perfil
          </button>
        }
      </div>

      <div class="mt-3 border-t border-slate-800 pt-3">
        @if (isAuthenticated()) {
          @if (isAdmin()) {
            <div class="mb-3 inline-flex rounded-full border border-rose-400/30 bg-rose-500/15 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-rose-200">
              Admin
            </div>
          } @else {
            <div class="mb-3 flex flex-wrap items-center gap-2">
              @if (plan()) {
                <span [class]="getPlanClasses(plan())" class="rounded-full px-2.5 py-1 text-xs font-black uppercase tracking-wide">
                  {{ getPlanLabel(plan()) }}
                </span>
              }
              <span class="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
                <svg class="h-4 w-4 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.86-9.81a.75.75 0 0 0-1.21-.88l-3.49 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.06 0l4-5.5Z" clip-rule="evenodd" />
                </svg>
                <span class="font-extrabold text-white">@if (credits() > 900) { &infin; } @else { {{ credits() }} }</span>
                <span class="text-slate-400">créditos</span>
              </span>
            </div>
          }

          <button
            type="button"
            (click)="onMobileLogoutClick()"
            class="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white">
            Sair
          </button>
        } @else {
          <div class="grid grid-cols-1 gap-2">
            <button
              type="button"
              (click)="onMobileRegisterClick()"
              class="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white">
              Criar conta
            </button>
            <button
              type="button"
              (click)="onMobileLoginClick()"
              class="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-md shadow-blue-900/40 transition hover:brightness-110">
              Login
            </button>
          </div>
        }
      </div>
    </div>
  }
</header>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class HeaderComponent {
  isAuthenticated = input<boolean>(false);
  isAdmin = input<boolean>(false);
  credits = input<number>(0);
  plan = input<UserPlan | undefined>();
  isPlansActive = input<boolean>(false);
  isProfileActive = input<boolean>(false);

  requestLogin = output<void>();
  requestRegister = output<void>();
  logout = output<void>();
  viewPlans = output<void>();
  viewProfile = output<void>();
  requestLanding = output<void>();

  readonly isMobileMenuOpen = signal(false);

  toggleMobileMenu() {
    this.isMobileMenuOpen.update((open) => !open);
  }

  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
  }

  onLoginClick() {
    this.requestLogin.emit();
  }

  onRegisterClick() {
    this.requestRegister.emit();
  }

  onLogoutClick() {
    this.logout.emit();
  }

  onViewPlansClick() {
    this.viewPlans.emit();
  }

  onViewProfileClick() {
    this.viewProfile.emit();
  }

  onLogoClick() {
    this.requestLanding.emit();
  }

  onMobileLandingClick() {
    this.closeMobileMenu();
    this.onLogoClick();
  }

  onMobilePlansClick() {
    this.closeMobileMenu();
    this.onViewPlansClick();
  }

  onMobileProfileClick() {
    this.closeMobileMenu();
    this.onViewProfileClick();
  }

  onMobileLoginClick() {
    this.closeMobileMenu();
    this.onLoginClick();
  }

  onMobileRegisterClick() {
    this.closeMobileMenu();
    this.onRegisterClick();
  }

  onMobileLogoutClick() {
    this.closeMobileMenu();
    this.onLogoutClick();
  }

  getPlanClasses(plan: UserPlan | undefined): string {
    switch (plan) {
      case 'pro':
        return 'border border-blue-300/30 bg-blue-500/20 text-blue-100';
      case 'intermediario':
        return 'border border-indigo-300/30 bg-indigo-500/20 text-indigo-100';
      case 'basico':
        return 'border border-emerald-300/30 bg-emerald-500/20 text-emerald-100';
      default:
        return 'border border-slate-600 bg-slate-800 text-slate-100';
    }
  }

  getPlanLabel(plan: UserPlan | undefined): string {
    switch (plan) {
      case 'basico':
        return 'Básico';
      case 'intermediario':
        return 'Intermediário';
      case 'pro':
        return 'Pro';
      default:
        return 'Gratuito';
    }
  }
}
